// Scoped per RUM error source so identical text from another source is kept.
const NOISE_ERROR_PATTERNS_BY_SOURCE: ReadonlyMap<string, readonly string[]> = new Map([
  // Cached pre-fix bundles that still console.error autoplay rejections.
  ['console', ['Failed to play notification sound: NotAllowedError']],
  // Stale X/Twitter conversion tag in GTM fires twq() without the base pixel.
  ['source', ['twq is not defined', "Can't find variable: twq"]],
]);

// Mirrors REDACTED_REQUEST_PARAMS in appquickchat/logging_utils/credential_redaction.py.
// RUM runs trackResources, so the browser reports the export request URL and would carry a
// merchant-entered value about their own visitors into Datadog even though the server redacts its copy.
const REDACTED_URL_PARAMS: readonly string[] = ['metadata_value'];
const REDACTED_VALUE = '<redacted>';
// Whole parameter names only, so a key that merely ends in one keeps its value.
const REDACTED_URL_PARAM_PATTERN = new RegExp(
  `([?&])(${REDACTED_URL_PARAMS.join('|')})=[^&\\s]*`,
  'gi',
);

interface RumEventLike {
  type?: string;
  error?: { message?: string; source?: string; resource?: { url?: string } | null } | null;
  resource?: { url?: string } | null;
  view?: { url?: string; referrer?: string } | null;
}

export function redactRumUrl(url: string): string {
  return url.replace(REDACTED_URL_PARAM_PATTERN, `$1$2=${REDACTED_VALUE}`);
}

// Per field, so one hostile getter cannot leave the remaining fields unscrubbed.
function redactInPlace<T extends object, K extends keyof T>(
  target: T | null | undefined,
  key: K,
): void {
  try {
    const value = target?.[key];
    if (typeof value === 'string') {
      target[key] = redactRumUrl(value) as T[K];
    }
  } catch {
    // An unreadable field is one Datadog's own serializer cannot read either.
  }
}

// Every RUM field that can carry a request URL; a missed one is a silent leak, so cover them all.
export function redactRumEventUrls(event: RumEventLike | null | undefined): void {
  redactInPlace(event?.resource, 'url');
  redactInPlace(event?.view, 'url');
  redactInPlace(event?.view, 'referrer');
  try {
    redactInPlace(event?.error?.resource, 'url');
  } catch {
    // `error` itself was unreadable; the fields above are still scrubbed.
  }
}

export function isNoiseRumErrorEvent(event: RumEventLike | null | undefined): boolean {
  if (event?.type !== 'error') return false;
  const source = event.error?.source;
  const message = event.error?.message;
  if (typeof source !== 'string' || typeof message !== 'string') return false;
  const patterns = NOISE_ERROR_PATTERNS_BY_SOURCE.get(source) ?? [];
  return patterns.some((pattern) => message.includes(pattern));
}

// beforeSend: return true to keep, false to discard; fail open so a throw here can never kill error collection.
export function dropNoiseRumEvents(event: RumEventLike | null | undefined): boolean {
  try {
    redactRumEventUrls(event);
    return !isNoiseRumErrorEvent(event);
  } catch {
    return true;
  }
}
